import React from "react";
import AuralisDevHealth from "@/pages/admin/AuralisDevHealth";

// ────────────────────────────────────────────────────────────────
// SynkTribe Admin Dashboard — MVP scaffold (no chart deps)
// - Tailwind for styling
// - Minimal inline SVG charts (MiniBarChart, MiniLineWithBand)
// - Mock hooks → swap with Firestore/Functions later
// ────────────────────────────────────────────────────────────────

// Types
interface MetricPoint { t: number; v: number }
interface SQIStat { vendor: string; model: string; medianSQI: number; n: number }
interface PairingStat { when: string; claims: number; successRate: number; p50: number; p95: number }

// ─────────────── Mini SVG Charts (drop-in, no deps) ───────────────
function MiniBarChart({
  data, xKey, yKey, height = 180,
}: { data: any[]; xKey: string; yKey: string; height?: number }) {
  const width = 640;
  const pad = 24;
  const W = width - pad * 2;
  const H = height - pad * 2;
  const maxY = Math.max(1, ...data.map(d => Number(d[yKey]) || 0));
  const bw = W / Math.max(1, data.length);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
      <g transform={`translate(${pad},${pad})`}>
        {data.map((d, i) => {
          const v = Number(d[yKey]) || 0;
          const h = (v / maxY) * H;
          return (
            <rect
              key={i}
              x={i * bw + 2}
              y={H - h}
              width={bw - 4}
              height={h}
              fill="currentColor"
              opacity={0.7}
              rx={4}
            />
          );
        })}
        {/* x labels every few bars */}
        {data.map((d, i) =>
          i % Math.ceil(data.length / 8 || 1) === 0 ? (
            <text
              key={`lbl-${i}`}
              x={i * bw + bw / 2}
              y={H + 12}
              textAnchor="middle"
              className="fill-white/60 text-[10px]"
            >
              {String(d[xKey])}
            </text>
          ) : null
        )}
      </g>
    </svg>
  );
}

function MiniLineWithBand({
  data, xKey, yKey, loKey, hiKey, height = 220,
}: { data: any[]; xKey: string; yKey: string; loKey: string; hiKey: string; height?: number }) {
  const width = 720;
  const pad = 28;
  const W = width - pad * 2;
  const H = height - pad * 2;

  const ys = data.flatMap(d => [
    Number(d[loKey]) ?? 0,
    Number(d[hiKey]) ?? 0,
    Number(d[yKey]) ?? 0,
  ]);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const x = (i: number) => pad + (i / Math.max(1, data.length - 1)) * W;
  const y = (v: number) =>
    pad + (1 - (v - minY) / Math.max(1e-6, maxY - minY)) * H;

  const bandPath = (() => {
    if (!data.length) return "";
    const top = data
      .map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(Number(d[hiKey]) || 0)}`)
      .join(" ");
    const bottom = [...data]
      .reverse()
      .map((d, rIndex) => {
        const i = data.length - 1 - rIndex;
        return `L ${x(i)} ${y(Number(d[loKey]) || 0)}`;
      })
      .join(" ");
    return `${top} ${bottom} Z`;
  })();

  const linePath = data
    .map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(Number(d[yKey]) || 0)}`)
    .join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
      <path d={bandPath} fill="currentColor" opacity={0.15} />
      <path d={linePath} fill="none" stroke="currentColor" strokeWidth={2} />
      {/* sparse x labels */}
      {data.map((d, i) =>
        i % Math.ceil(data.length / 8 || 1) === 0 ? (
          <text
            key={i}
            x={x(i)}
            y={height - 6}
            textAnchor="middle"
            className="fill-white/60 text-[10px]"
          >
            {String(d[xKey]).slice(0, 6)}
          </text>
        ) : null
      )}
    </svg>
  );
}

// ───────────────── Hooks (mocks; wire to Firestore/Functions later) ─────────────────
function useRulesMeta() {
  const hash =
    (window as any).__RULES_HASH__ ??
    (typeof (globalThis as any).__RULES_HASH__ !== "undefined"
      ? (globalThis as any).__RULES_HASH__
      : "unknown");
  const at =
    (window as any).__RULES_DEPLOYED_AT__ ??
    (typeof (globalThis as any).__RULES_DEPLOYED_AT__ !== "undefined"
      ? (globalThis as any).__RULES_DEPLOYED_AT__
      : "unknown");
  return { hash: String(hash), deployedAt: String(at) };
}

function useReadinessHistogram() {
  const buckets = Array.from({ length: 10 }, (_, i) => ({
    bucket: `${i * 10}-${i * 10 + 9}`,
    count: Math.max(5, (Math.sin(i) * 20 + 30) | 0),
  }));
  return { buckets };
}

function useSQIByVendor(): { rows: SQIStat[] } {
  const rows: SQIStat[] = [
    { vendor: "Lumina", model: "Vibe", medianSQI: 0.82, n: 1243 },
    { vendor: "Oura", model: "Gen3", medianSQI: 0.74, n: 2120 },
    { vendor: "Garmin", model: "Venu 3", medianSQI: 0.77, n: 980 },
  ];
  return { rows };
}

function usePairingStats(): { series: PairingStat[] } {
  const series: PairingStat[] = Array.from({ length: 14 }, (_, d) => ({
    when: new Date(Date.now() - (13 - d) * 86400000).toISOString().slice(0, 10),
    claims: 100 + Math.round(Math.random() * 80),
    successRate: 0.9 - Math.abs(7 - d) * 0.01,
    p50: 5 + Math.round(Math.random() * 2),
    p95: 12 + Math.round(Math.random() * 4),
  }));
  return { series };
}

function useDiagnosticsRecent() {
  const rows = Array.from({ length: 12 }, (_, i) => ({
    id: `diag-${i}`,
    uid: `user_${1000 + i}`,
    device:
      i % 3 === 0 ? "Lumina Vibe" : i % 3 === 1 ? "Oura Gen3" : "Garmin Venu 3",
    readiness: 60 + ((i * 7) % 35),
    flags: [i % 4 === 0 ? "light-leak" : null, i % 5 === 0 ? "motion" : null].filter(
      Boolean
    ) as string[],
    at: new Date(Date.now() - i * 3600_000).toISOString(),
  }));
  return { rows };
}

function useConfidenceTimeline(uid?: string) {
  const base = Date.now() - 60 * 60 * 1000;
  const data: Array<{
    t: string;
    hr: number;
    hrLo: number;
    hrHi: number;
    conf: number;
  }> = Array.from({ length: 60 }, (_, i) => {
    const t = new Date(base + i * 60 * 1000);
    const hr = 70 + Math.round(8 * Math.sin(i / 7));
    const conf = 0.6 + 0.4 * Math.max(0, Math.sin(i / 10));
    const spread = Math.round((1 - conf) * 12);
    return {
      t: t.toLocaleTimeString(),
      hr,
      hrLo: hr - spread,
      hrHi: hr + spread,
      conf: Number(conf.toFixed(2)),
    };
  });
  return { data };
}

// ────────────────────────── UI primitives ──────────────────────────
function Card(
  props: React.PropsWithChildren<{
    title?: string;
    subtitle?: string;
    right?: React.ReactNode;
  }>
) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 dark:bg-black/30 p-4 shadow-sm">
      {(props.title || props.right) && (
        <div className="flex items-center justify-between mb-2">
          <div>
            {props.title && (
              <div className="text-base font-semibold">{props.title}</div>
            )}
            {props.subtitle && (
              <div className="text-xs opacity-70 -mt-0.5">{props.subtitle}</div>
            )}
          </div>
          {props.right}
        </div>
      )}
      {props.children}
    </div>
  );
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 dark:bg-black/30 p-3">
      <div className="text-xs opacity-70">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
      {hint && <div className="text-[11px] opacity-60">{hint}</div>}
    </div>
  );
}

// ────────────────────────── Cards / Charts ─────────────────────────
function RulesStatusCard() {
  const { hash, deployedAt } = useRulesMeta();
  return (
    <Card title="Firestore Rules" subtitle="Active policy">
      <div className="text-xs font-mono break-all">hash: {hash}</div>
      <div className="text-xs opacity-80">deployed: {deployedAt}</div>
      <div className="mt-3 text-xs opacity-70">
        CI should stamp __RULES_HASH__ / __RULES_DEPLOYED_AT__.
      </div>
    </Card>
  );
}

function ReadinessHistogram() {
  const { buckets } = useReadinessHistogram();
  return (
    <Card title="Readiness Score Distribution" subtitle="Onboarding Fit & Fidelity">
      <div className="h-48">
        <MiniBarChart data={buckets} xKey="bucket" yKey="count" />
      </div>
    </Card>
  );
}

function SQIByVendorChart() {
  const { rows } = useSQIByVendor();
  return (
    <Card title="Median SQI by Vendor/Model" subtitle="Higher is better">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left opacity-70">
            <tr>
              <th className="py-2 pr-4">Vendor</th>
              <th className="py-2 pr-4">Model</th>
              <th className="py-2 pr-4">Median SQI</th>
              <th className="py-2 pr-4">n</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-white/10">
                <td className="py-2 pr-4">{r.vendor}</td>
                <td className="py-2 pr-4">{r.model}</td>
                <td className="py-2 pr-4 font-mono">{r.medianSQI.toFixed(2)}</td>
                <td className="py-2 pr-4 font-mono">{r.n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PairingStatsCard() {
  const { series } = usePairingStats();
  const avgSuccess = Math.round(
    (series.reduce((a, b) => a + b.successRate, 0) / Math.max(1, series.length)) *
      100
  );
  const median = (key: keyof PairingStat) => {
    const arr = series.map(s => Number(s[key]) || 0).sort((a, b) => a - b);
    return arr[Math.floor(arr.length / 2)] ?? 0;
  };
  const lineData = series.map(s => ({ t: s.when, v: s.p50, lo: s.p50 - 1, hi: s.p50 + 1 }));

  return (
    <Card title="Pairing Success & Latency" subtitle="Last 14 days">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <StatTile label="Avg Success" value={`${avgSuccess}%`} hint="last 14d" />
        <StatTile label="Median p50 Claim" value={`${median("p50")}s`} />
        <StatTile label="Median p95 Claim" value={`${median("p95")}s`} />
      </div>
      <div className="h-56">
        <MiniLineWithBand
          data={lineData}
          xKey="t"
          yKey="v"
          loKey="lo"
          hiKey="hi"
        />
      </div>
    </Card>
  );
}

function DiagnosticsTable() {
  const { rows } = useDiagnosticsRecent();
  return (
    <Card title="Recent Diagnostics" subtitle="Fit & Fidelity checks">
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left opacity-70">
            <tr>
              <th className="py-2 pr-4">User</th>
              <th className="py-2 pr-4">Device</th>
              <th className="py-2 pr-4">Readiness</th>
              <th className="py-2 pr-4">Flags</th>
              <th className="py-2 pr-4">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/10">
                <td className="py-2 pr-4 font-mono">{r.uid}</td>
                <td className="py-2 pr-4">{r.device}</td>
                <td className="py-2 pr-4 font-mono">{r.readiness}</td>
                <td className="py-2 pr-4">
                  {r.flags.length === 0 ? (
                    <span className="text-xs opacity-60">none</span>
                  ) : (
                    <div className="flex gap-1 flex-wrap">
                      {r.flags.map((f) => (
                        <span
                          key={f}
                          className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-500/30"
                        >
                          {String(f)}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-4 text-xs opacity-80">
                  {new Date(r.at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ConfidenceTimeline({ uid }: { uid?: string }) {
  const { data } = useConfidenceTimeline(uid);
  const mapped = data.map(d => ({ t: d.t, v: d.hr, lo: d.hrLo, hi: d.hrHi }));
  return (
    <Card title="Session Confidence Timeline" subtitle="HR with CI">
      <div className="h-56">
        <MiniLineWithBand data={mapped} xKey="t" yKey="v" loKey="lo" hiKey="hi" />
      </div>
    </Card>
  );
}

// ────────────────────────── Pages ──────────────────────────
function OverviewPage() {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <div className="xl:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatTile label="Active 24h" value="1,284" hint="users" />
        <StatTile label="Pairing Success" value="93%" hint="last 24h" />
        <StatTile label="Events / min" value="612" hint="streams" />
      </div>
      <RulesStatusCard />
      <ReadinessHistogram />
      <SQIByVendorChart />
      <PairingStatsCard />
      <DiagnosticsTable />
    </div>
  );
}

function DevicesPairingPage() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <PairingStatsCard />
      <SQIByVendorChart />
      <DiagnosticsTable />
    </div>
  );
}

function StreamsPage() {
  return (
    <div className="grid grid-cols-1 gap-4">
      <ConfidenceTimeline />
      <Card title="Live Tail" subtitle="Recent symbolic events (mock)">
        <div className="text-sm opacity-70">
          TODO: virtualized list with filters for kind/source/category.
        </div>
      </Card>
    </div>
  );
}

function DiagnosticsPage() {
  return (
    <div className="grid grid-cols-1 gap-4">
      <Card title="Run Quality Check">
        <div className="text-sm opacity-80">
          This will guide the user through light-leak, motion, and breathing calibration
          steps, then compute Readiness + SQI.
        </div>
        <div className="mt-3">
          <button className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15">
            Start 60-second check
          </button>
        </div>
      </Card>
      <DiagnosticsTable />
    </div>
  );
}

function VendorReportsPage() {
  return (
    <div className="grid grid-cols-1 gap-4">
      <SQIByVendorChart />
      <Card title="Top Failure Contexts" subtitle="Aggregated & de-identified">
        <ul className="list-disc pl-6 text-sm">
          <li>Bright outdoor light → optical contrast loss</li>
          <li>High motion during HRV capture → widened CI</li>
          <li>Cold ambient temperature → vasoconstriction</li>
        </ul>
      </Card>
    </div>
  );
}

function DevToolsPage() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Unicode Inspector" subtitle="Paste text to visualize codepoints">
        <div className="text-sm opacity-70">Hook up your glyphguard.ts helpers here.</div>
      </Card>
      <Card title="Function Pingers" subtitle="Call startBlePairing / verifyWearable with canned payloads">
        <div className="text-sm opacity-70">
          Wire to Functions with fetch(__CF_BASE__ + "/verifyWearable").
        </div>
      </Card>
    </div>
  );
}

// ────────────────────────── Shell ──────────────────────────
const NAV = [
  { key: "overview", label: "Overview", comp: OverviewPage },
  { key: "devices", label: "Devices & Pairing", comp: DevicesPairingPage },
  { key: "streams", label: "Streams", comp: StreamsPage },
  { key: "diagnostics", label: "Diagnostics", comp: DiagnosticsPage },
  { key: "vendors", label: "Vendor Reports", comp: VendorReportsPage },
  { key: "dev", label: "Dev Tools", comp: DevToolsPage },
] as const;

type NavKey = typeof NAV[number]["key"];

export default function AdminDashboard() {
  const [tab, setTab] = React.useState<NavKey>("overview");
  const Active = NAV.find((n) => n.key === tab)!.comp;

  return (
    <div className="min-h-screen text-white bg-gradient-to-b from-slate-900 to-black">
      <header className="sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-black/30 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="font-semibold tracking-wide">SynkTribe • Admin</div>
          <div className="text-xs opacity-70">
            build{" "}
            <span className="font-mono">
              {(globalThis as any).__APP_VERSION__ ?? "0.0.0"}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <nav className="lg:sticky lg:top-[72px] self-start">
          <div className="rounded-2xl border border-white/10 bg-white/5 dark:bg-black/30 p-2">
            {NAV.map((n) => (
              <button
                key={n.key}
                onClick={() => setTab(n.key)}
                className={`w-full text-left px-3 py-2 rounded-xl mb-1 border transition ${
                  tab === n.key
                    ? "bg-white/10 border-white/20"
                    : "border-transparent hover:bg-white/5"
                }`}
              >
                {n.label}
              </button>
            ))}
          </div>
        </nav>

        <main className="pb-20">
          <Active />
        </main>
      </div>
    </div>
  );
}
